/**
 * NsectionAPage
 * A.摘要與適用性（A6.4-FORM-METH-002 SECTION A）
 * 三頁籤：A.1 標題與版本 / A.2 前次提交參照 / A.3 摘要描述
 * 每個頁籤獨立儲存，資料以 Card 呈現
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import nsectionAService, { NsectionADetail, NsectionAUpdate } from '../services/nsectionAService';
import RichTextEditor from '../components/RichTextEditor';
import DateInput from '../components/DateInput';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';

const NsectionAPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh-TW';
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { selectedProposalId, selectedProposal } = useProject();
  const toast = useToast();
  const hasInitialized = useRef(false);

  const [data, setData] = useState<NsectionADetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<1 | 2 | 3>(1);

  // 表單狀態
  const [formData, setFormData] = useState({
    formal_title: '',
    submission_date: '',
    version_number: '',
    previous_reference: '',
    summary_description: '',
  });

  // 初始化
  useEffect(() => {
    if (!hasInitialized.current && !permissionLoading) {
      validateSession('NsectionAPage');
      hasInitialized.current = true;
    }
  }, [permissionLoading]);

  // 載入資料
  useEffect(() => {
    if (!selectedProposalId) {
      setData(null);
      setFormData({ formal_title: '', submission_date: '', version_number: '', previous_reference: '', summary_description: '' });
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await nsectionAService.getByProposal(selectedProposalId);
        if (result) {
          setData(result);
          setFormData({
            formal_title: selectedProposal?.title || result.formal_title || '',
            submission_date: result.submission_date || '',
            version_number: result.version_number || '',
            previous_reference: result.previous_reference || '',
            summary_description: result.summary_description || '',
          });
        } else {
          setData(null);
          setFormData({
            formal_title: selectedProposal?.title || '',
            submission_date: '',
            version_number: '',
            previous_reference: '',
            summary_description: '',
          });
        }
      } catch (err: any) {
        if (err.response?.status === 404) {
          setData(null);
          setFormData({
            formal_title: selectedProposal?.title || '',
            submission_date: '', version_number: '',
            previous_reference: '', summary_description: '',
          });
        } else {
          setError(err.response?.data?.detail || (isZh ? '載入資料失敗' : 'Failed to load data'));
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedProposalId, selectedProposal, isZh]);

  // 依頁籤儲存
  const handleSave = async () => {
    if (!selectedProposalId) return;
    try {
      setLoading(true);
      let updateData: NsectionAUpdate = {};
      if (activeTab === 1) {
        updateData = {
          formal_title: formData.formal_title || undefined,
          submission_date: formData.submission_date || undefined,
          version_number: formData.version_number || undefined,
        };
      } else if (activeTab === 2) {
        updateData = { previous_reference: formData.previous_reference || undefined };
      } else if (activeTab === 3) {
        updateData = { summary_description: formData.summary_description || undefined };
      }
      const result = await nsectionAService.update(selectedProposalId, updateData);
      setData(result);
      toast.success(isZh ? '儲存成功' : 'Saved successfully');
    } catch (err: any) {
      toast.error(err.response?.data?.detail || (isZh ? '儲存失敗' : 'Save failed'));
    } finally {
      setLoading(false);
    }
  };

  // 權限
  if (permissionLoading) {
    return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  }
  if (!hasPermission('nsection_a', 'read')) {
    return <div className="page-container"><div className="error-message">{t('common.noPermission')}</div></div>;
  }
  const canUpdate = hasPermission('nsection_a', 'update');

  const fieldStyle: React.CSSProperties = { marginBottom: '20px' };
  const labelStyle: React.CSSProperties = { display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '14px' };
  const inputStyle: React.CSSProperties = { width: '100%', padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' };
  const cardStyle: React.CSSProperties = {
    width: '100%', background: '#fff', borderRadius: '10px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #e8e8e8', padding: '28px 32px',
  };
  const cardTitleStyle: React.CSSProperties = { fontSize: '16px', fontWeight: 700, color: '#333', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #f0f0f0' };

  const tabs = [
    { key: 1 as const, zh: 'A.1.標題與版本', en: 'A.1. Title, submission date and version' },
    { key: 2 as const, zh: 'A.2.前次提交參照', en: 'A.2. Previous submission reference' },
    { key: 3 as const, zh: 'A.3.摘要描述', en: 'A.3. Summary description' },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <FunctionPageHeader funcCode="nsection_a" />
      </div>

      {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}

      {!selectedProposalId ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: '16px' }}>
          {isZh ? '請先在上方選擇方法論提案' : 'Please select a methodology proposal from the top bar'}
        </div>
      ) : loading && !data ? (
        <div className="loading">{t('common.loading')}</div>
      ) : (
        <div>
          {/* 頁籤 */}
          <div style={{ display: 'flex', borderBottom: '2px solid #e0e0e0', marginBottom: '20px' }}>
            {tabs.map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
                padding: '10px 24px', border: 'none', cursor: 'pointer', fontSize: '14px', marginBottom: '-2px',
                borderBottom: activeTab === tab.key ? '3px solid #4A90E2' : '3px solid transparent',
                background: activeTab === tab.key ? '#f0f7ff' : 'transparent',
                color: activeTab === tab.key ? '#4A90E2' : '#666', fontWeight: activeTab === tab.key ? 600 : 400,
              }}>
                {isZh ? tab.zh : tab.en}
              </button>
            ))}
          </div>

          {/* Tab 1: A.1 標題與版本 */}
          {activeTab === 1 && (
            <div style={cardStyle}>
              <div style={cardTitleStyle}>
                {isZh ? 'A.1. 標題與版本' : 'A.1. Title, submission date and version'}
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>{isZh ? '方法論正式標題' : 'Title'}</label>
                <div style={{ ...inputStyle, background: '#f5f5f5', minHeight: '36px', display: 'flex', alignItems: 'center' }}
                  dangerouslySetInnerHTML={{ __html: formData.formal_title || '<span style="color:#999">' + (isZh ? '（從方法論提案標題帶入）' : '(From methodology proposal title)') + '</span>' }}
                />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>{isZh ? '提交日期' : 'Submission date'}</label>
                <DateInput value={formData.submission_date} disabled={!canUpdate}
                  onChange={(v) => setFormData({ ...formData, submission_date: v })} />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>{isZh ? '版本號' : 'Version'}</label>
                <input style={inputStyle} value={formData.version_number} disabled={!canUpdate}
                  onChange={(e) => setFormData({ ...formData, version_number: e.target.value })}
                  placeholder={isZh ? '例如：01.0' : 'e.g., 01.0'} />
              </div>
              {canUpdate && (
                <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
                  <button className="btn-primary" onClick={handleSave} disabled={loading} style={{ padding: '8px 32px' }}>
                    {loading ? (isZh ? '儲存中...' : 'Saving...') : t('common.save')}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Tab 2: A.2 前次提交參照 */}
          {activeTab === 2 && (
            <div style={cardStyle}>
              <div style={cardTitleStyle}>
                {isZh ? 'A.2. 前次提交參照' : 'A.2. Previous submission reference'}
              </div>
              <div style={{ fontSize: '13px', color: '#666', marginBottom: '16px', padding: '10px 14px', background: '#f9f9f9', borderRadius: '6px' }}>
                {isZh
                  ? '若本方法論基於先前提交或已核准的 Article 6.4 方法論，請說明參考編號及主要差異與理由。'
                  : 'If this methodology is based on a previous submission or an approved Article 6.4 methodology, please state the reference numbers. Explain briefly the main differences and their rationale.'}
              </div>
              <div style={fieldStyle}>
                <RichTextEditor
                  value={formData.previous_reference}
                  onChange={(html) => setFormData({ ...formData, previous_reference: html })}
                  disabled={!canUpdate}
                  placeholder={isZh ? '請輸入前次提交參照說明...' : 'Enter previous submission reference...'}
                />
              </div>
              {canUpdate && (
                <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
                  <button className="btn-primary" onClick={handleSave} disabled={loading} style={{ padding: '8px 32px' }}>
                    {loading ? (isZh ? '儲存中...' : 'Saving...') : t('common.save')}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Tab 3: A.3 摘要描述 */}
          {activeTab === 3 && (
            <div style={cardStyle}>
              <div style={cardTitleStyle}>
                {isZh ? 'A.3. 摘要描述' : 'A.3. Summary description'}
              </div>
              <div style={{ fontSize: '13px', color: '#666', marginBottom: '16px', padding: '10px 14px', background: '#f9f9f9', borderRadius: '6px' }}>
                {isZh
                  ? '方法論或方法學工具的摘要描述，包含主要基線與監測方法學步驟。'
                  : 'Summary description of the methodology or methodological tool, including major baseline and monitoring methodological steps.'}
              </div>
              <div style={fieldStyle}>
                <RichTextEditor
                  value={formData.summary_description}
                  onChange={(html) => setFormData({ ...formData, summary_description: html })}
                  disabled={!canUpdate}
                  placeholder={isZh ? '請輸入摘要描述...' : 'Enter summary description...'}
                />
              </div>
              {canUpdate && (
                <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
                  <button className="btn-primary" onClick={handleSave} disabled={loading} style={{ padding: '8px 32px' }}>
                    {loading ? (isZh ? '儲存中...' : 'Saving...') : t('common.save')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NsectionAPage;
